import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, Input, ViewChild, inject } from '@angular/core'
import {
  ControlContainer,
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators
} from '@angular/forms'
import {TooltipModule} from "ngx-bootstrap/tooltip";
import {AutosizeModule} from "ngx-autosize";
import {QuillModule} from "ngx-quill";
import {NgMultiSelectDropDownModule} from "ng-multiselect-dropdown";
import {KeyValuePipe, NgForOf, NgIf} from '@angular/common';
import {TranslatePipe} from '@ngx-translate/core';
import {ConvertFormValueToReadOnlyDisplayPipe} from '../../pipes/convert-form-value-to-read-only-display.pipe';

export type SelectOption = { key: string | number, value: string | null | number, disabled?: boolean };

@Component({
  selector: 'app-form-group',
  templateUrl: './form-group.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: FormGroupComponent,
    multi: true
  }],
  imports: [
    ReactiveFormsModule,
    TooltipModule,
    AutosizeModule,
    QuillModule,
    NgMultiSelectDropDownModule,
    NgIf,
    TranslatePipe,
    NgForOf,
    KeyValuePipe,
    ConvertFormValueToReadOnlyDisplayPipe,
  ],
  standalone: true
})
export class FormGroupComponent implements ControlValueAccessor {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly injector = inject(Injector);


  @Input() public label!: string
  @Input() public labelCheckboxEnabler: boolean = false;
  @Input() public small = false

  @Input() public errorMessages!: Record<string, string>
  @Input() public name!: string
  @Input() public inline = false

  @Input() public type: 'text' | 'checkbox' | 'password' | 'email' | 'select' | 'textarea' | 'textarea-html' | 'number' | 'file' | 'readonly' | 'radio' | 'switch-checkbox' = 'text'
  @Input() public prefix?: string
  /**
   * das select wird immer eine array von values ausgeben (bei multi === true)
   * order EIN value bei  !multi
   */
  @Input() public selectOptions: SelectOption[] = []

  @Input() public multi = false

  @Input() public tooltipText?: string
  @Input() public autocomplete?: string
  @Input() public numberPattern = ''
  @Input() public numberMin = 0
  @Input() public numberMax?: number
  @Input() public tabindex?: number
  @Input() public dateMin?: string
  @Input() public dateMax?: string
  @Input() public inputCounter = false
  @Input() public autofocus = false
  @Input() public minRows = 3
  @Input() public inputCounterMax: number | null = null
  @Input() public placeholder = ''
  @Input() public radioGroupName = '';

  @Input() public centerCheckbox = false;

  public requiredValidator = Validators.required
  /**
   * @see https://github.com/nileshpatel17/ng-multiselect-dropdown#settings
   */

  public showPassword = false;


  @ViewChild(FormControlDirective, {static: true})
  public formControlDirective?: FormControlDirective;

  @Input()
  public formControl?: FormControl;

  @Input()
  public formControlName!: string;

  public get control(): FormControl {
    if (this.formControl) {
      return this.formControl
    }
    const controlCont = this.controlContainer
    if (this.formControlName && controlCont?.control !== null) {
      return controlCont?.control.get(this.formControlName) as FormControl;
    }
    return new FormControl(null)
  }

  get controlContainer(): ControlContainer {
    return this.injector.get(ControlContainer);
  }

  registerOnTouched(fn: () => void): void {
    this.formControlDirective?.valueAccessor?.registerOnTouched(fn);
  }

  registerOnChange(fn: () => void): void {
    this.formControlDirective?.valueAccessor?.registerOnChange(fn);
  }

  writeValue(obj: () => void): void {
    this.formControlDirective?.valueAccessor?.writeValue(obj);
  }

  setDisabledState(): void {
    // da formControl und control identisch sind brauchen wir hier nichts weiter zu geben

  }

  public toggleFormState(): void {
    if (this.control.enabled) {
      this.control.reset();
      this.control.disable();
    } else {
      this.control.enable();
    }
    this.cdr.markForCheck();
  }


}
